import { convertQueryObjToString, localisedValue } from "utils"
import parentApi from "store/parentApi"
import { transformCalendarEvents } from "./calendarTransformer"
import { createPrivilegeObject } from "utils/privileges/privilegesMapper"
import dayjs from "dayjs"
import { t } from "i18next"

const calendarApi = parentApi.injectEndpoints({
  endpoints: build => ({
    getCalenderEvents: build.query({
      query: params => {
        return `/calendarEvents?${convertQueryObjToString(params)}`
      },
      transformResponse: response => transformCalendarEvents(response),
      providesTags: ["calender-events"],
    }),
    getEventsForCalendar: build.query({
      query: params => {
        return `/calendarEvents?${convertQueryObjToString(params)}`
      },
      transformResponse: (response, meta, arg) => {
        if (arg?.afterTime) {
          const afterDate = dayjs(arg?.afterTime)
          return {
            ...response,
            saleEvents: response?.saleEvents?.map(event => {
              const isLive = dayjs().isBetween(dayjs(event?.startTimeStamp), dayjs(event?.endTimeStamp), null, "[]")
              const isPast = dayjs().isAfter(dayjs(event?.endTimeStamp))
              return {
                ...event,
                name: `${event?.name} (${isPast ? t("Past") : isLive ? t("Live") : t("Scheduled")})`,
              }
            }),
          }
        }
        return response
      },
      providesTags: ["calender-events"],
    }),
    getEventDetails: build.query({
      query: params => `/calendarEvents/${params?.id}?eventType=${params?.type}`,
    }),
    getManagementMeetingDetails: build.query({
      query: id => `/managementMeetings/${id}`,
      transformResponse: response => ({
        ...response.data,
        privileges: createPrivilegeObject(response?.data?.privileges),
      }),
    }),
    getInsights: build.query({
      query: params => ({
        url: `/calendarEvents/insights?${convertQueryObjToString(params)}`,
      }),
    }),
    addEditSalesEvent: build.mutation({
      query: ({ values, id, user }) => {
        const method = id ? "PUT" : "POST"
        const startDate = dayjs(
          (values?.eventDate ? values.eventDate?.[0].format("YYYY-MM-DD") : "") +
            " " +
            (values?.eventTime ? values?.eventTime?.[0].format("HH:mm:ss") : "")
        )
        const endDate = dayjs(
          (values?.eventDate ? values.eventDate?.[1].format("YYYY-MM-DD") : "") +
            " " +
            (values?.eventTime ? values?.eventTime?.[1].format("HH:mm:ss") : "")
        )
        let body = {
          ...(id && { id }),
          name: values.name,
          location: values.location,
          agencyId: user?.AgencyConfiguration?.agencyId,
          startDateTime: startDate,
          endDateTime: endDate,
          relaxedAmount: +values?.tokenRelaxation,
          relaxationType: "token",
          ...(+values?.tokenRelaxation == 1 && { minAmount: +values?.minToken }),
          relaxedAmountExpiryDays: values?.tokenExpiry,
          projectIds: values?.project ? values?.project : [],
          cityIds: values?.city ? values?.city : [],
          ...(values?.locationUrl && { locationURL: values?.locationUrl }),
          cityId: values?.eventCity,
          eventVenueId: values?.eventVenue,
          ...(values?.companyOffice && { agencyOfficeId: +values?.companyOffice }),
          ...(values?.eventProject && { projectId: +values?.eventProject }),
          ...(values?.latitude && { latitude: values?.latitude }),
          ...(values?.longitude && { longitude: values?.longitude }),
        }
        return {
          url: `/calendarEvents/salesEvent`,
          method,
          body,
        }
      },
      invalidatesTags: (result, error) => (!error ? ["calender-events"] : []),
    }),
    addUnavailability: build.mutation({
      query: values => {
        return { url: `/calendarEvents`, method: "POST", body: values }
      },
      invalidatesTags: (result, error, args) => {
        return result ? ["calender-events"] : []
      },
    }),
    deleteUnavailabilityItem: build.mutation({
      query: id => {
        return { url: `/calendarEvents/${id}`, method: "DELETE" }
      },
      invalidatesTags: (result, error, args) => {
        return result ? ["calender-events"] : []
      },
    }),
    deleteSaleEventItem: build.mutation({
      query: id => {
        return { url: `/calendarEvents/salesEvent/${id}`, method: "DELETE" }
      },
      invalidatesTags: (result, error, args) => {
        return result ? ["calender-events"] : []
      },
    }),
    deleteManagementMeetingEvent: build.mutation({
      query: id => ({ url: `managementMeetings/${id}`, method: "DELETE" }),
      invalidatesTags: (result, error, args) => {
        return result ? ["calender-events"] : []
      },
    }),
    getSalesEventVenues: build.query({
      query: () => `/calendarEvents/saleEventVenues`,
      transformResponse: response =>
        response?.map(item => ({
          ...item,
          label: localisedValue({ object: item, key: "name" }),
          value: item?.id?.toString(),
        })),
    }),
  }),
})

export const {
  useAddEditSalesEventMutation,
  useAddUnavailabilityMutation,
  useLazyGetCalenderEventsQuery,
  useGetCalenderEventsQuery,
  useGetEventsForCalendarQuery,
  useLazyGetEventsForCalendarQuery,
  useLazyGetEventDetailsQuery,
  useGetInsightsQuery,
  useLazyGetInsightsQuery,
  useDeleteUnavailabilityItemMutation,
  useDeleteSaleEventItemMutation,
  useGetManagementMeetingDetailsQuery,
  useLazyGetManagementMeetingDetailsQuery,
  useDeleteManagementMeetingEventMutation,
  useGetSalesEventVenuesQuery,
  useLazyGetSalesEventVenuesQuery,
} = calendarApi
